import type { LeagueMatch } from '../../api/predictions.ts';

/**
 * ¿Se puede editar el pronóstico de este partido?
 *
 * Es una función aparte, y no una expresión dentro del componente, para poder fijar por
 * test la única regla que importa: **la decide el servidor**.
 *
 * `is_open` lo calcula PostgreSQL comparando `kickoff_time` con su propio `now()`. Aquí
 * NO se vuelve a comparar con la hora del dispositivo. La diferencia parece inocente
 * porque casi siempre coinciden, pero el reloj del cliente puede ir atrasado —o estar
 * cambiado a propósito— y entonces la interfaz invitaría a pronosticar un partido ya
 * empezado, para acabar en un 409 y en la sensación de que la aplicación falla.
 *
 * El caso del partido anulado se mira aparte porque el servidor ya lo incluye en
 * `is_open`, pero el mensaje que hay que enseñar es otro (R10).
 */
export function puedeEditar(match: Pick<LeagueMatch, 'is_open' | 'status'>): boolean {
  return match.is_open && match.status !== 'cancelled';
}

/** Un partido con desenlace conocido enseña marcador; uno por jugar, no. */
export function tieneResultado(
  match: Pick<LeagueMatch, 'actual_home_score' | 'actual_away_score'>,
): boolean {
  return match.actual_home_score !== null && match.actual_away_score !== null;
}

/**
 * ¿Se pueden ver los pronósticos del resto de la liga?
 *
 * Lo dice el servidor con un campo PROPIO, no con la negación del plazo. Escribir
 * `!match.is_open` parece equivalente y no lo es: con R9 un partido aplazado está
 * cerrado, y si eso revelara los pronósticos ajenos, al anunciarse la fecha nueva el
 * plazo se reabriría con todo el mundo habiendo visto ya lo que puso el resto.
 *
 * Adelantarse aquí es peor que en el formulario: el backend devuelve 409 y no manda los
 * datos, así que lo único que se consigue es una petición fallida y un hueco vacío.
 */
export function puedeVerAjenos(match: Pick<LeagueMatch, 'predictions_visible'>): boolean {
  return match.predictions_visible;
}

/**
 * ¿A qué pestaña del filtro pertenece el partido?
 *
 * Se apoya en `predictions_visible` y NO en `is_open`. Con R9, un aplazado deja de estar
 * abierto, y clasificarlo por `!is_open` lo mandaría a "Joués" —un partido que no se ha
 * jugado, listado entre los jugados—. Con `predictions_visible` cae donde tiene que caer:
 * sigue por venir, porque su fecha está por anunciar.
 */
export function esPorJugar(match: Pick<LeagueMatch, 'predictions_visible'>): boolean {
  return !match.predictions_visible;
}

/**
 * R9: la hora que se conoce de un partido aplazado es la vieja, y se sabe que ya no vale.
 * Pintarla sería mentir con precisión de minuto. Hasta que el proveedor mande la nueva
 * fecha —y con ella el partido vuelva a `scheduled`— solo se puede decir que está
 * aplazado.
 */
export function fechaEsFiable(match: Pick<LeagueMatch, 'status'>): boolean {
  return match.status !== 'postponed';
}
